import { Component, Input, Output, EventEmitter, OnInit, OnChanges, SimpleChanges, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ButtonsModule } from '@progress/kendo-angular-buttons';
import { TooltipModule } from '@progress/kendo-angular-tooltip';
import { FileVaultApiService } from '../../services/file-vault-api.service';
import {
  WorkflowInstance,
  WorkflowStep,
  WorkflowProgress,
  WorkflowState,
  WorkflowStepState,
  WorkflowDecision
} from '../../models/file-vault.models';

export interface WorkflowStepClickEvent {
  step: WorkflowStep;
  stepIndex: number;
}

@Component({
  selector: 'fv-workflow-visualizer',
  standalone: true,
  template: `
    <div class="workflow-visualizer">
      <!-- Header -->
      <div class="header" *ngIf="showHeader">
        <div class="header-left">
          <h3 class="workflow-name">{{ workflowName }}</h3>
          <span class="workflow-state-badge" [ngClass]="getWorkflowStateBadgeClass()">
            {{ getWorkflowStateLabel() }}
          </span>
        </div>
        <div class="header-right" *ngIf="progress">
          <span class="progress-text">{{ progress.completedSteps }} of {{ progress.totalSteps }} steps completed</span>
          <div class="progress-bar">
            <div class="progress-fill" [style.width.%]="progress.percentComplete"></div>
          </div>
        </div>
      </div>

      <!-- Loading -->
      <div class="loading" *ngIf="isLoading">
        <span class="loading-spinner"></span>
        Loading workflow...
      </div>

      <!-- Steps visualization -->
      <div class="steps-container" *ngIf="!isLoading && displaySteps.length > 0">
        <!-- Horizontal mode -->
        <div class="steps-horizontal" *ngIf="orientation === 'horizontal'">
          <ng-container *ngFor="let step of displaySteps; let i = index; let last = last">
            <div class="step-node"
                 [ngClass]="getStepNodeClass(step)"
                 (click)="onStepClick(step, i)"
                 [class.clickable]="step.state !== WorkflowStepState.Pending">
              <div class="step-circle">
                <span class="step-icon">{{ getStepIcon(step.state) }}</span>
              </div>
              <div class="step-info">
                <span class="step-name">{{ step.stepName }}</span>
                <span class="step-meta">
                  <ng-container *ngIf="step.state === WorkflowStepState.Approved || step.state === WorkflowStepState.Rejected">
                    {{ step.completedBy }} | {{ step.completedAt | date:'shortDate' }}
                  </ng-container>
                  <ng-container *ngIf="step.state === WorkflowStepState.Pending || step.state === WorkflowStepState.InProgress">
                    {{ step.assignedToRole || step.assignedTo || 'Unassigned' }}
                  </ng-container>
                  <ng-container *ngIf="step.state === WorkflowStepState.Skipped">
                    Skipped
                  </ng-container>
                  <ng-container *ngIf="step.state === WorkflowStepState.Delegated">
                    Delegated to {{ step.delegatedTo }}
                  </ng-container>
                </span>
              </div>
            </div>
            <div class="step-connector" *ngIf="!last" [ngClass]="getConnectorClass(step)">
              <div class="connector-line"></div>
              <div class="connector-arrow"></div>
            </div>
          </ng-container>
        </div>

        <!-- Vertical mode -->
        <div class="steps-vertical" *ngIf="orientation === 'vertical'">
          <ng-container *ngFor="let step of displaySteps; let i = index; let last = last">
            <div class="step-row"
                 [ngClass]="getStepNodeClass(step)"
                 (click)="onStepClick(step, i)"
                 [class.clickable]="step.state !== WorkflowStepState.Pending">
              <div class="step-indicator">
                <div class="step-circle">
                  <span class="step-icon">{{ getStepIcon(step.state) }}</span>
                </div>
                <div class="step-line" *ngIf="!last" [ngClass]="getConnectorClass(step)"></div>
              </div>
              <div class="step-content">
                <div class="step-header">
                  <span class="step-name">{{ step.stepName }}</span>
                  <span class="step-state-badge" [ngClass]="getStepStateBadgeClass(step.state)">
                    {{ getStepStateLabel(step.state) }}
                  </span>
                </div>
                <div class="step-details">
                  <div class="detail-row" *ngIf="step.assignedTo || step.assignedToRole">
                    <span class="detail-label">Assigned:</span>
                    <span class="detail-value">{{ step.assignedTo || step.assignedToRole }}</span>
                  </div>
                  <div class="detail-row" *ngIf="step.dueDate">
                    <span class="detail-label">Due:</span>
                    <span class="detail-value" [ngClass]="{ 'overdue': isStepOverdue(step) }">
                      {{ step.dueDate | date:'shortDate' }}
                    </span>
                  </div>
                  <div class="detail-row" *ngIf="step.completedAt">
                    <span class="detail-label">Completed:</span>
                    <span class="detail-value">{{ step.completedAt | date:'short' }} by {{ step.completedBy }}</span>
                  </div>
                  <div class="detail-row comments" *ngIf="step.comments">
                    <span class="detail-label">Comments:</span>
                    <span class="detail-value">"{{ step.comments }}"</span>
                  </div>
                </div>
              </div>
            </div>
          </ng-container>
        </div>
      </div>

      <!-- Empty state -->
      <div class="empty-state" *ngIf="!isLoading && displaySteps.length === 0">
        <p>No workflow steps to display</p>
      </div>

      <!-- Entity info footer -->
      <div class="entity-footer" *ngIf="showEntityInfo && workflow">
        <span class="entity-label">For:</span>
        <span class="entity-type">{{ workflow.entityType }}</span>
        <a class="entity-link" (click)="onEntityClick()">
          {{ workflow.entityNumber || workflow.entityId | slice:0:8 }}
        </a>
        <span class="entity-title" *ngIf="workflow.entityTitle">- {{ workflow.entityTitle }}</span>
      </div>
    </div>
  `,
  styles: [`
    .workflow-visualizer {
      padding: 16px;
    }

    .header {
      display: flex;
      justify-content: space-between;
      align-items: center;
      margin-bottom: 24px;
      flex-wrap: wrap;
      gap: 12px;
    }

    .header-left {
      display: flex;
      align-items: center;
      gap: 12px;
    }

    .workflow-name {
      margin: 0;
      font-size: 18px;
      font-weight: 600;
      color: var(--fv-text);
    }

    .workflow-state-badge {
      display: inline-block;
      padding: 4px 10px;
      border-radius: 12px;
      font-size: 12px;
      font-weight: 500;
    }

    .workflow-state-badge.pending { background: var(--fv-status-draft-bg); color: var(--fv-status-draft-fg); }
    .workflow-state-badge.in-progress { background: var(--fv-status-info-bg); color: var(--fv-status-info-fg); }
    .workflow-state-badge.completed { background: var(--fv-status-success-bg); color: var(--fv-status-success-fg); }
    .workflow-state-badge.cancelled { background: var(--fv-surface-alt); color: var(--fv-text-muted); }
    .workflow-state-badge.failed { background: var(--fv-status-error-bg); color: var(--fv-status-error-fg); }

    .header-right {
      display: flex;
      align-items: center;
      gap: 12px;
    }

    .progress-text {
      font-size: 13px;
      color: var(--fv-text-secondary);
    }

    .progress-bar {
      width: 120px;
      height: 6px;
      background: var(--fv-border);
      border-radius: 3px;
      overflow: hidden;
    }

    .progress-fill {
      height: 100%;
      background: #4caf50;
      transition: width 0.3s ease;
    }

    .loading {
      display: flex;
      align-items: center;
      justify-content: center;
      gap: 8px;
      padding: 48px;
      color: var(--fv-text-secondary);
    }

    .loading-spinner {
      width: 20px;
      height: 20px;
      border: 2px solid var(--fv-border);
      border-top-color: var(--fv-text-accent);
      border-radius: 50%;
      animation: spin 0.8s linear infinite;
    }

    @keyframes spin {
      to { transform: rotate(360deg); }
    }

    /* Horizontal steps */
    .steps-horizontal {
      display: flex;
      align-items: flex-start;
      overflow-x: auto;
      padding: 16px 0;
    }

    .step-node {
      display: flex;
      flex-direction: column;
      align-items: center;
      min-width: 120px;
    }

    .step-node.clickable {
      cursor: pointer;
    }

    .step-node.clickable:hover .step-circle {
      transform: scale(1.1);
    }

    .step-circle {
      width: 48px;
      height: 48px;
      border-radius: 50%;
      background: var(--fv-border);
      display: flex;
      align-items: center;
      justify-content: center;
      margin-bottom: 8px;
      transition: transform 0.2s ease, background 0.2s ease;
      border: 3px solid transparent;
    }

    .step-node.current .step-circle {
      background: var(--fv-status-info-bg);
      border-color: var(--fv-text-accent);
      animation: pulse 2s infinite;
    }

    .step-node.approved .step-circle { background: var(--fv-status-success-bg); border-color: #4caf50; }
    .step-node.rejected .step-circle { background: var(--fv-status-error-bg); border-color: #f44336; }
    .step-node.skipped .step-circle { background: var(--fv-status-warning-bg); border-color: #ff9800; }
    .step-node.delegated .step-circle { background: var(--fv-status-purple-bg); border-color: #9c27b0; }

    @keyframes pulse {
      0%, 100% { box-shadow: 0 0 0 0 rgba(25, 118, 210, 0.4); }
      50% { box-shadow: 0 0 0 8px rgba(25, 118, 210, 0); }
    }

    .step-icon {
      font-size: 20px;
      font-weight: bold;
    }

    .step-node.approved .step-icon { color: var(--fv-status-success-fg); }
    .step-node.rejected .step-icon { color: var(--fv-status-error-fg); }
    .step-node.current .step-icon { color: var(--fv-text-accent); }
    .step-node.skipped .step-icon { color: var(--fv-status-warning-fg); }
    .step-node.delegated .step-icon { color: var(--fv-status-purple-fg); }
    .step-node.pending .step-icon { color: var(--fv-text-muted); }

    .step-info {
      text-align: center;
    }

    .step-name {
      display: block;
      font-weight: 500;
      font-size: 13px;
      color: var(--fv-text);
      margin-bottom: 4px;
    }

    .step-meta {
      font-size: 11px;
      color: var(--fv-text-muted);
    }

    .step-connector {
      display: flex;
      align-items: center;
      padding: 0 4px;
      margin-top: 24px;
    }

    .connector-line {
      width: 40px;
      height: 2px;
      background: var(--fv-border);
    }

    .step-connector.completed .connector-line {
      background: #4caf50;
    }

    .connector-arrow {
      width: 0;
      height: 0;
      border-top: 5px solid transparent;
      border-bottom: 5px solid transparent;
      border-left: 8px solid var(--fv-border);
    }

    .step-connector.completed .connector-arrow {
      border-left-color: #4caf50;
    }

    /* Vertical steps */
    .steps-vertical {
      display: flex;
      flex-direction: column;
    }

    .step-row {
      display: flex;
      gap: 16px;
      padding: 8px 0;
    }

    .step-row.clickable {
      cursor: pointer;
    }

    .step-row.clickable:hover .step-content {
      background: var(--fv-surface-hover);
    }

    .step-indicator {
      display: flex;
      flex-direction: column;
      align-items: center;
      width: 48px;
      flex-shrink: 0;
    }

    .step-row .step-circle {
      margin-bottom: 0;
    }

    .step-line {
      flex: 1;
      width: 2px;
      background: var(--fv-border);
      margin: 4px 0;
      min-height: 30px;
    }

    .step-line.completed {
      background: #4caf50;
    }

    .step-content {
      flex: 1;
      padding: 8px 12px;
      border-radius: 8px;
      transition: background 0.2s ease;
    }

    .step-header {
      display: flex;
      align-items: center;
      gap: 8px;
      margin-bottom: 8px;
    }

    .step-row .step-name {
      text-align: left;
      margin: 0;
    }

    .step-state-badge {
      display: inline-block;
      padding: 2px 8px;
      border-radius: 4px;
      font-size: 10px;
      font-weight: 600;
      text-transform: uppercase;
    }

    .step-state-badge.pending { background: var(--fv-status-draft-bg); color: var(--fv-status-draft-fg); }
    .step-state-badge.in-progress { background: var(--fv-status-info-bg); color: var(--fv-status-info-fg); }
    .step-state-badge.approved { background: var(--fv-status-success-bg); color: var(--fv-status-success-fg); }
    .step-state-badge.rejected { background: var(--fv-status-error-bg); color: var(--fv-status-error-fg); }
    .step-state-badge.skipped { background: var(--fv-status-warning-bg); color: var(--fv-status-warning-fg); }
    .step-state-badge.delegated { background: var(--fv-status-purple-bg); color: var(--fv-status-purple-fg); }

    .step-details {
      display: flex;
      flex-direction: column;
      gap: 4px;
    }

    .detail-row {
      display: flex;
      gap: 8px;
      font-size: 12px;
    }

    .detail-label {
      color: var(--fv-text-muted);
      min-width: 70px;
    }

    .detail-value {
      color: var(--fv-text);
    }

    .detail-value.overdue {
      color: var(--fv-status-error-fg);
      font-weight: 500;
    }

    .detail-row.comments {
      margin-top: 4px;
    }

    .detail-row.comments .detail-value {
      font-style: italic;
      color: var(--fv-text-secondary);
    }

    .empty-state {
      padding: 48px;
      text-align: center;
      color: var(--fv-text-muted);
    }

    .entity-footer {
      margin-top: 24px;
      padding-top: 16px;
      border-top: 1px solid var(--fv-border);
      font-size: 13px;
      color: var(--fv-text-secondary);
    }

    .entity-label {
      margin-right: 4px;
    }

    .entity-type {
      font-weight: 500;
      margin-right: 4px;
    }

    .entity-link {
      color: var(--fv-text-accent);
      cursor: pointer;
      font-family: monospace;
    }

    .entity-link:hover {
      text-decoration: underline;
    }

    .entity-title {
      color: var(--fv-text);
    }
  `],
  imports: [CommonModule, ButtonsModule, TooltipModule]
})
export class WorkflowVisualizerComponent implements OnInit, OnChanges {
  private api = inject(FileVaultApiService);

  // Expose enum for template
  WorkflowStepState = WorkflowStepState;

  /** Workflow instance to display */
  @Input() workflow?: WorkflowInstance;

  /** Alternatively, provide workflow instance ID to load */
  @Input() workflowId?: string;

  /** For entity - load workflow by entity */
  @Input() entityType?: string;
  @Input() entityId?: string;

  /** Display orientation */
  @Input() orientation: 'horizontal' | 'vertical' = 'vertical';

  /** Show header with name and progress */
  @Input() showHeader = true;

  /** Show entity info footer */
  @Input() showEntityInfo = true;

  /** Emitted when a step is clicked */
  @Output() stepClick = new EventEmitter<WorkflowStepClickEvent>();

  /** Emitted when entity link is clicked */
  @Output() entityClick = new EventEmitter<{ entityType: string; entityId: string }>();

  progress?: WorkflowProgress;
  isLoading = false;

  ngOnInit(): void {
    this.loadWorkflow();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['workflowId'] || changes['entityType'] || changes['entityId']) {
      this.loadWorkflow();
    }
  }

  async loadWorkflow(): Promise<void> {
    // If workflow already provided, just load progress
    if (this.workflow) {
      this.loadProgress();
      return;
    }

    // Load by ID
    if (this.workflowId) {
      this.isLoading = true;
      try {
        this.workflow = await this.api.getWorkflow(this.workflowId, true).toPromise();
        this.loadProgress();
      } catch (error) {
        console.error('Failed to load workflow', error);
      } finally {
        this.isLoading = false;
      }
      return;
    }

    // Load by entity
    if (this.entityType && this.entityId) {
      this.isLoading = true;
      try {
        this.workflow = await this.api.getWorkflowForEntity(this.entityType, this.entityId).toPromise();
        this.loadProgress();
      } catch (error) {
        // No active workflow - that's ok
        console.log('No active workflow for entity');
      } finally {
        this.isLoading = false;
      }
    }
  }

  async loadProgress(): Promise<void> {
    if (!this.workflow?.id) return;

    try {
      this.progress = await this.api.getWorkflowProgress(this.workflow.id).toPromise();
    } catch (error) {
      console.error('Failed to load workflow progress', error);
    }
  }

  get workflowName(): string {
    return this.workflow?.templateName ?? this.workflow?.template?.name ?? 'Workflow';
  }

  get displaySteps(): WorkflowStep[] {
    return this.workflow?.steps?.sort((a, b) => a.stepIndex - b.stepIndex) ?? [];
  }

  get currentStepIndex(): number {
    return this.workflow?.currentStepIndex ?? 0;
  }

  onStepClick(step: WorkflowStep, index: number): void {
    if (step.state !== WorkflowStepState.Pending) {
      this.stepClick.emit({ step, stepIndex: index });
    }
  }

  onEntityClick(): void {
    if (this.workflow) {
      this.entityClick.emit({
        entityType: this.workflow.entityType,
        entityId: this.workflow.entityId
      });
    }
  }

  // Helper methods
  getStepNodeClass(step: WorkflowStep): string {
    const classes: string[] = [];

    switch (step.state) {
      case WorkflowStepState.Approved:
        classes.push('approved');
        break;
      case WorkflowStepState.Rejected:
        classes.push('rejected');
        break;
      case WorkflowStepState.Skipped:
        classes.push('skipped');
        break;
      case WorkflowStepState.Delegated:
        classes.push('delegated');
        break;
      case WorkflowStepState.InProgress:
        classes.push('current');
        break;
      case WorkflowStepState.Pending:
        classes.push('pending');
        break;
    }

    // Mark current step
    if (step.stepIndex === this.currentStepIndex && step.state === WorkflowStepState.Pending) {
      classes.push('current');
    }

    return classes.join(' ');
  }

  getStepIcon(state: WorkflowStepState): string {
    switch (state) {
      case WorkflowStepState.Approved: return '\u2713'; // checkmark
      case WorkflowStepState.Rejected: return '\u2717'; // x
      case WorkflowStepState.Skipped: return '\u21B7'; // arrow
      case WorkflowStepState.Delegated: return '\u21C4'; // double arrow
      case WorkflowStepState.InProgress: return '\u25B6'; // play
      case WorkflowStepState.Pending: return '\u25CB'; // circle
      default: return '\u25CB';
    }
  }

  getStepStateLabel(state: WorkflowStepState): string {
    switch (state) {
      case WorkflowStepState.Pending: return 'Pending';
      case WorkflowStepState.InProgress: return 'In Progress';
      case WorkflowStepState.Approved: return 'Approved';
      case WorkflowStepState.Rejected: return 'Rejected';
      case WorkflowStepState.Skipped: return 'Skipped';
      case WorkflowStepState.Delegated: return 'Delegated';
      default: return 'Unknown';
    }
  }

  getStepStateBadgeClass(state: WorkflowStepState): string {
    switch (state) {
      case WorkflowStepState.Pending: return 'pending';
      case WorkflowStepState.InProgress: return 'in-progress';
      case WorkflowStepState.Approved: return 'approved';
      case WorkflowStepState.Rejected: return 'rejected';
      case WorkflowStepState.Skipped: return 'skipped';
      case WorkflowStepState.Delegated: return 'delegated';
      default: return '';
    }
  }

  getConnectorClass(step: WorkflowStep): string {
    if (step.state === WorkflowStepState.Approved ||
        step.state === WorkflowStepState.Skipped) {
      return 'completed';
    }
    return '';
  }

  getWorkflowStateLabel(): string {
    if (!this.workflow) return '';
    switch (this.workflow.state) {
      case WorkflowState.Pending: return 'Pending';
      case WorkflowState.InProgress: return 'In Progress';
      case WorkflowState.Completed: return 'Completed';
      case WorkflowState.Cancelled: return 'Cancelled';
      case WorkflowState.Failed: return 'Failed';
      default: return 'Unknown';
    }
  }

  getWorkflowStateBadgeClass(): string {
    if (!this.workflow) return '';
    switch (this.workflow.state) {
      case WorkflowState.Pending: return 'pending';
      case WorkflowState.InProgress: return 'in-progress';
      case WorkflowState.Completed: return 'completed';
      case WorkflowState.Cancelled: return 'cancelled';
      case WorkflowState.Failed: return 'failed';
      default: return '';
    }
  }

  isStepOverdue(step: WorkflowStep): boolean {
    if (!step.dueDate) return false;
    if (step.state === WorkflowStepState.Approved ||
        step.state === WorkflowStepState.Rejected ||
        step.state === WorkflowStepState.Skipped) {
      return false;
    }
    return new Date(step.dueDate) < new Date();
  }
}
